/**
 * Photo capture and downscaling.
 *
 * Ported from Csa/offline/photos.js, unchanged — this logic has nothing CSA-
 * specific in it. Downscaling is mandatory, not an optimisation. A tablet
 * photo is 3–5 MB; two photos across a few hundred checkpoints is potentially
 * gigabytes, which exhausts the IndexedDB quota on site — where it is
 * unrecoverable — and makes sync unusable over an office connection.
 *
 * Every capture produces two blobs:
 *
 *   display   ~1600 px longest edge, JPEG q0.72, roughly 250–350 KB.
 *             What the report embeds and what the client eventually sees.
 *   thumbnail ~200 px, JPEG q0.6, roughly 6–10 KB. Checkpoint lists and the
 *             camera roll render only these, so scrolling never decodes a
 *             full-size blob.
 *
 * The blob is stored once and referenced from each checkpoint it evidences —
 * elec_photo_link is many-to-many for exactly that reason. Storing a copy
 * per checkpoint would multiply the offline footprint for nothing.
 */

const DISPLAY_EDGE = 1600;
const DISPLAY_QUALITY = 0.72;
const THUMB_EDGE = 200;
const THUMB_QUALITY = 0.6;

/**
 * Decode a file into something drawable.
 *
 * `imageOrientation: "from-image"` is the part that matters: a tablet held
 * sideways writes an EXIF rotation flag rather than rotating the pixels, and
 * a canvas ignores it. Without this, half the photos in a report are on
 * their side. The <img> fallback applies orientation natively, which is why
 * it is the fallback rather than the primary path.
 */
async function decode(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (e) {
      // Older Safari rejects the options argument rather than ignoring it.
      try {
        return await createImageBitmap(file);
      } catch (e2) {
        /* fall through to the <img> path */
      }
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file is not an image this browser can read"));
    };
    img.src = url;
  });
}

const sizeOf = (src) => ({
  width: src.width || src.naturalWidth,
  height: src.height || src.naturalHeight,
});

function render(src, maxEdge, quality) {
  const { width, height } = sizeOf(src);
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d");
  // Downscaling by 3–4x without this is visibly aliased, and legible text in
  // a photo of a nameplate or a permit is often the whole point of the
  // evidence.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, 0, 0, w, h);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve({ blob, width: w, height: h });
        else reject(new Error("Could not process that image"));
      },
      "image/jpeg",
      quality
    );
  });
}

/**
 * Turn a camera file into the two blobs that get stored.
 *
 * JPEG, never PNG — a photograph gains nothing from lossless encoding and
 * costs far more to render.
 */
export async function processPhoto(file) {
  if (!file) throw new Error("No photo was chosen");
  if (!/^image\//.test(file.type || "")) throw new Error("That file is not an image");

  const src = await decode(file);
  try {
    const [display, thumb] = await Promise.all([
      render(src, DISPLAY_EDGE, DISPLAY_QUALITY),
      render(src, THUMB_EDGE, THUMB_QUALITY),
    ]);

    return {
      blob: display.blob,
      thumb: thumb.blob,
      width: display.width,
      height: display.height,
      bytes: display.blob.size,
      thumb_bytes: thumb.blob.size,
      original_bytes: file.size,
    };
  } finally {
    // ImageBitmap holds decoded pixels — several MB for a tablet photo —
    // until it is closed, and the GC is in no hurry.
    if (src && typeof src.close === "function") src.close();
  }
}

/**
 * A URL for showing a stored blob.
 *
 * The caller must revoke it. Object URLs are not garbage collected while the
 * document lives, so a camera roll that creates one per thumbnail on every
 * render leaks the whole roll.
 */
export function blobUrl(blob) {
  if (!blob) return null;
  return URL.createObjectURL(blob);
}

export function revoke(url) {
  if (url) {
    try {
      URL.revokeObjectURL(url);
    } catch (e) {
      /* already gone */
    }
  }
}

export const LIMITS = {
  DISPLAY_EDGE,
  DISPLAY_QUALITY,
  THUMB_EDGE,
  THUMB_QUALITY,
};
